import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { ReadOnlyNotice } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';
import { pillClass } from '../utils/filterPill';

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const parseIsoDate = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d || 1);
};

const STATUS_STYLES = {
    renewed: 'bg-green-100 text-green-800 border-green-200',
    payment_pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    open: 'bg-blue-50 text-blue-700 border-blue-200',
    not_renewed: 'bg-red-100 text-red-800 border-red-200',
    renewed_no_order: 'bg-orange-100 text-orange-800 border-orange-200',
    switched_to_flexible: 'bg-teal-50 text-teal-700 border-teal-200',
};

// Most urgent first: a customer shows the worst status among its due subscriptions.
const STATUS_PRIORITY = ['not_renewed', 'renewed_no_order', 'payment_pending', 'open', 'switched_to_flexible', 'renewed'];
const TILE_FOR_STATUS = { renewed: 'renewed', switched_to_flexible: 'renewed', payment_pending: 'open', open: 'open', not_renewed: 'not_renewed', renewed_no_order: 'renewed_no_order' };

const statusLabel = (status, t) => ({
    renewed: t?.renewal_renewed || 'Renewed',
    payment_pending: t?.renewal_payment_pending || 'Payment pending',
    open: t?.renewal_open || 'Open',
    not_renewed: t?.renewal_not_renewed || 'Not renewed',
    renewed_no_order: t?.renewal_no_order || 'Renewed without order',
    switched_to_flexible: t?.renewal_switched || 'Switched to flexible',
}[status] || status);

const renewalSetting = (renewalType, t) => {
    const type = String(renewalType || '');
    if (type.startsWith('CANCEL')) return t?.renewal_setting_cancel || 'Cancels at end date';
    if (type.startsWith('SWITCH_TO')) return t?.renewal_setting_switch || 'Switches to flexible plan';
    if (type.startsWith('AUTO_RENEW') || type.startsWith('RENEW')) return t?.renewal_setting_renew || 'Auto-renews';
    return '';
};

const StatusBadge = ({ status, t }) => (
    <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border whitespace-nowrap ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
        title={status === 'renewed_no_order' ? t?.renewal_no_order_hint : undefined}
    >
        {statusLabel(status, t)}
    </span>
);

const RenewalStatusSkeleton = ({ columns }) => (
    <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 h-[88px]">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-7 bg-gray-200 rounded w-12 mt-3"></div>
                </div>
            ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-[#8b236a] text-white">
                    <tr>{columns.map((c, i) => <th key={i} className="px-4 py-3 text-left font-medium">{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {[1, 2, 3, 4, 5, 6].map(row => (
                        <tr key={row}>
                            <td className="px-4 py-3 w-8"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-40"></div><div className="h-3 bg-gray-100 rounded w-28 mt-1.5"></div></td>
                            <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-16"></div></td>
                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8"></div></td>
                            <td className="px-4 py-3"><div className="h-5 bg-gray-200 rounded-full w-24"></div></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const AdminRenewalStatus = ({ googleIdToken, t, language }) => {
    const [month, setMonth] = useState(() => monthKey(new Date()));
    const [paymentType, setPaymentType] = useState('all');
    const [tileFilter, setTileFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState(() => new Set());
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const loadedMonths = useRef(new Map());

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());
    const locale = language === 'en' ? 'en-US' : 'nl-NL';
    const formatDate = (iso) => parseIsoDate(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

    useEffect(() => {
        if (!googleIdToken) return undefined;
        setError(null);
        if (loadedMonths.current.has(month)) {
            setData(loadedMonths.current.get(month));
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        setIsLoading(true);
        apiRequest(`/api/admin/renewal-status?month=${month}`)
            .then(res => {
                loadedMonths.current.set(month, res);
                if (!cancelled) setData(res);
            })
            .catch(err => {
                if (cancelled) return;
                setData(null);
                setError(err);
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [apiRequest, googleIdToken, month]);

    const monthPills = useMemo(() => {
        const now = new Date();
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() + i - 3, 1);
            const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : '';
            return { key: monthKey(d), label: `${d.toLocaleString(locale, { month: 'short' })}${year}` };
        });
    }, [locale]);

    const customers = useMemo(() => {
        const byCustomer = new Map();
        (data?.rows || []).forEach(row => {
            if (!byCustomer.has(row.customerId)) {
                byCustomer.set(row.customerId, {
                    customerId: row.customerId,
                    customerName: row.customerName,
                    customerDomain: row.customerDomain,
                    isPostpaid: row.isPostpaid,
                    subs: [],
                });
            }
            byCustomer.get(row.customerId).subs.push(row);
        });
        return Array.from(byCustomer.values()).map(c => {
            const status = STATUS_PRIORITY.find(s => c.subs.some(sub => sub.status === s)) || 'open';
            return {
                ...c,
                status,
                tile: TILE_FOR_STATUS[status],
                renewedCount: c.subs.filter(sub => sub.status === 'renewed').length,
                firstDue: c.subs.reduce((min, sub) => (sub.dueDate < min ? sub.dueDate : min), c.subs[0].dueDate),
            };
        });
    }, [data]);

    const byPaymentType = customers.filter(c =>
        paymentType === 'all' || (paymentType === 'postpaid' ? c.isPostpaid : !c.isPostpaid));

    const tileCounts = {};
    byPaymentType.forEach(c => { tileCounts[c.tile] = (tileCounts[c.tile] || 0) + 1; });
    const tiles = [
        { key: 'all', label: t?.renewal_due || 'Due', count: byPaymentType.length, accent: 'text-gray-800' },
        { key: 'renewed', label: t?.renewal_renewed || 'Renewed', count: tileCounts.renewed || 0, accent: 'text-green-700' },
        { key: 'open', label: t?.renewal_open || 'Open', count: tileCounts.open || 0, accent: 'text-blue-700' },
        { key: 'not_renewed', label: t?.renewal_not_renewed || 'Not renewed', count: tileCounts.not_renewed || 0, accent: 'text-red-700' },
    ];
    if (tileCounts.renewed_no_order) {
        tiles.push({ key: 'renewed_no_order', label: t?.renewal_no_order || 'Renewed without order', count: tileCounts.renewed_no_order, accent: 'text-orange-700' });
    }

    const search = searchTerm.trim().toLowerCase();
    const visibleCustomers = byPaymentType.filter(c =>
        (tileFilter === 'all' || c.tile === tileFilter) &&
        (!search ||
            (c.customerName || '').toLowerCase().includes(search) ||
            (c.customerDomain || '').toLowerCase().includes(search) ||
            c.subs.some(sub => (sub.skuName || '').toLowerCase().includes(search))));

    const toggleExpanded = (customerId) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(customerId)) next.delete(customerId); else next.add(customerId);
            return next;
        });
    };

    const selectMonth = (key) => {
        if (key === month) return;
        const loaded = loadedMonths.current.get(key);
        if (loaded) setData(loaded);
        else if (googleIdToken) setIsLoading(true);
        setMonth(key);
        setTileFilter('all');
        setExpanded(new Set());
    };

    let trackingNotice = null;
    if (data && !data.trackingSince) {
        trackingNotice = t?.renewal_tracking_pending;
    } else if (data && data.trackingSince > `${month}-01`) {
        trackingNotice = (t?.renewal_tracking_partial || 'Renewals are tracked since {date}.').replace('{date}', formatDate(data.trackingSince));
    }

    const columns = ['', t?.customer_name || 'Customer', t?.renewal_type || 'Type', t?.renewal_date || 'Renewal date', t?.renewal_subscriptions || 'Subscriptions', t?.status || 'Status'];
    const monthTitle = parseIsoDate(`${month}-01`).toLocaleString(locale, { month: 'long', year: 'numeric' });

    let content;
    if (isLoading) {
        content = <RenewalStatusSkeleton columns={columns} />;
    } else if (error) {
        content = (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
                {errorText(error, t, t?.renewal_load_failed || 'Failed to load renewal status.')}
            </div>
        );
    } else {
        content = (
            <div className="space-y-6">
                {trackingNotice && (
                    <div className="px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">{trackingNotice}</div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {tiles.map(tile => (
                        <button
                            key={tile.key}
                            onClick={() => setTileFilter(tile.key)}
                            className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors hover:border-[#8b236a]/40 ${tileFilter === tile.key ? 'border-[#8b236a] ring-1 ring-[#8b236a]' : 'border-gray-100'}`}
                        >
                            <div className="text-sm text-gray-500">{tile.label}</div>
                            <div className={`text-2xl font-bold mt-1 ${tile.accent}`}>{tile.count}</div>
                        </button>
                    ))}
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-[#8b236a] text-white">
                            <tr>{columns.map((c, i) => <th key={i} className="px-4 py-3 text-left font-medium whitespace-nowrap">{c}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {visibleCustomers.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-10 text-center text-gray-500">
                                        {customers.length === 0 ? (t?.renewal_none || 'No customers are due to renew in this month.') : (t?.renewal_none_filtered || 'No customers match these filters.')}
                                    </td>
                                </tr>
                            )}
                            {visibleCustomers.map(c => {
                                const isOpen = expanded.has(c.customerId);
                                return (
                                    <React.Fragment key={c.customerId}>
                                        <tr onClick={() => toggleExpanded(c.customerId)} className="hover:bg-purple-50 cursor-pointer transition-colors">
                                            <td className="px-4 py-3 w-8">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); toggleExpanded(c.customerId); }}
                                                    aria-expanded={isOpen}
                                                    aria-label={c.customerName}
                                                    className="text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8b236a] rounded"
                                                >
                                                    <svg className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Link
                                                    to={`/admin/customers/${c.customerId}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="font-medium text-gray-900 hover:text-[#8b236a] hover:underline"
                                                >
                                                    {c.customerName}
                                                </Link>
                                                {c.customerDomain && c.customerDomain !== c.customerName && (
                                                    <div className="text-xs text-gray-500">{c.customerDomain}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                    {c.isPostpaid ? (t?.postpaid || 'Postpaid') : (t?.prepaid || 'Prepaid')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-gray-700">{formatDate(c.firstDue)}</td>
                                            <td className="px-4 py-3 text-gray-700">{c.subs.length}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <StatusBadge status={c.status} t={t} />
                                                    {c.subs.length > 1 && c.renewedCount > 0 && c.status !== 'renewed' && (
                                                        <span className="text-xs text-gray-500 whitespace-nowrap">
                                                            {(t?.renewal_progress || '{renewed}/{total} renewed').replace('{renewed}', c.renewedCount).replace('{total}', c.subs.length)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="bg-gray-50">
                                                <td></td>
                                                <td colSpan={columns.length - 1} className="px-4 py-3">
                                                    <table className="min-w-full text-sm">
                                                        <thead>
                                                            <tr className="text-left text-xs text-gray-500">
                                                                <th className="py-1 pr-6 font-medium">{t?.sku || 'SKU / Product'}</th>
                                                                <th className="py-1 pr-6 font-medium">{t?.licenses || 'Licenses'}</th>
                                                                <th className="py-1 pr-6 font-medium">{t?.renewal_date || 'Renewal date'}</th>
                                                                <th className="py-1 pr-6 font-medium">{t?.renewal_order || 'Order'}</th>
                                                                <th className="py-1 font-medium">{t?.status || 'Status'}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {c.subs.map(sub => (
                                                                <tr key={`${sub.subscriptionId || sub.skuId}-${sub.dueDate}`}>
                                                                    <td className="py-1.5 pr-6 text-gray-800">
                                                                        {sub.skuName || sub.skuId || '—'}
                                                                        {sub.status === 'open' && renewalSetting(sub.renewalType, t) && (
                                                                            <div className="text-xs text-gray-500">{renewalSetting(sub.renewalType, t)}</div>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-1.5 pr-6 text-gray-600">{sub.licenses ?? '—'}</td>
                                                                    <td className="py-1.5 pr-6 text-gray-600 whitespace-nowrap">{formatDate(sub.dueDate)}</td>
                                                                    <td className="py-1.5 pr-6 text-gray-500 whitespace-nowrap">
                                                                        {sub.orderId ? <span title={sub.orderStatus}>{sub.orderId}</span> : '—'}
                                                                    </td>
                                                                    <td className="py-1.5"><StatusBadge status={sub.status} t={t} /></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t?.admin_renewal_status || 'Renewal Status'}
                    <span className="ml-3 text-lg font-normal text-gray-500 capitalize">{monthTitle}</span>
                </h1>
                <p className="text-gray-600 mt-2">{t?.renewal_status_description}</p>
            </div>

            <ReadOnlyNotice t={t} />

            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                    {monthPills.map(pill => (
                        <button key={pill.key} onClick={() => selectMonth(pill.key)} className={`${pillClass(month === pill.key)} capitalize`}>
                            {pill.label}
                        </button>
                    ))}
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => /^\d{4}-\d{2}$/.test(e.target.value) && selectMonth(e.target.value)}
                        className="border border-gray-300 rounded-full px-3 py-1 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8b236a]"
                        title={t?.renewal_other_month || 'Other month'}
                        aria-label={t?.renewal_other_month || 'Other month'}
                    />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex flex-wrap gap-2">
                        {[
                            ['all', t?.filter_all || 'All'],
                            ['prepaid', t?.prepaid || 'Prepaid'],
                            ['postpaid', t?.postpaid || 'Postpaid'],
                        ].map(([value, label]) => (
                            <button key={value} onClick={() => setPaymentType(value)} className={pillClass(paymentType === value)}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder={t?.search_customer || 'Search customer...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>

            {content}
        </div>
    );
};

export default AdminRenewalStatus;
